import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faThumbsUp, faThumbsDown} from '@fortawesome/free-solid-svg-icons';
import {useToggleVoteMutation} from '@api';
import {VoteTypeEnum, VotableEntityTypeEnum, VoteInfoResponse} from '@types';
import {useTheme} from '@components/theme/useTheme';

export type CommentVoteInfo = VoteInfoResponse;

type CommentVoteButtonsProps = {
    entityType: VotableEntityTypeEnum;
    entityId: number;
    voteInfo: CommentVoteInfo | null;
    onVoteChange?: (entityId: number, voteInfo: CommentVoteInfo) => void;
};

export const CommentVoteButtons = ({
    entityType,
    entityId,
    voteInfo,
    onVoteChange,
}: CommentVoteButtonsProps) => {
    const [toggleVote] = useToggleVoteMutation();
    const theme = useTheme();

    const getSelectedColors = () => {
        return theme === 'dark' ? 'text-neutral-200' : 'text-neutral-600';
    };

    const getUnselectedColors = () => {
        return theme === 'dark'
            ? 'text-neutral-500 hover:text-neutral-400'
            : 'text-neutral-400 hover:text-neutral-600';
    };

    const getLoadingColors = () => {
        return theme === 'dark' ? 'text-neutral-600' : 'text-neutral-300';
    };

    const handleVote = async (voteType: VoteTypeEnum) => {
        try {
            const result = await toggleVote({
                entityType,
                entityId,
                voteType,
            }).unwrap();

            if (onVoteChange) {
                onVoteChange(entityId, result);
            }
        } catch (error) {
            console.error('Vote failed:', error);
        }
    };

    if (!voteInfo) {
        return (
            <div className="flex items-center gap-3 text-xs">
                <div
                    className={`flex items-center gap-1 px-1 py-1 ${getLoadingColors()}`}>
                    <FontAwesomeIcon icon={faThumbsUp} />
                    <span>-</span>
                </div>
                <div
                    className={`flex items-center gap-1 px-1 py-1 ${getLoadingColors()}`}>
                    <FontAwesomeIcon icon={faThumbsDown} />
                    <span>-</span>
                </div>
            </div>
        );
    }

    return (
        <div className="flex items-center gap-2 text-xs">
            <button
                onClick={() => handleVote(VoteTypeEnum.UPVOTE)}
                className={`flex items-center gap-1 px-1 py-1 rounded text-xs transition-colors ${
                    voteInfo.userVote === VoteTypeEnum.UPVOTE
                        ? getSelectedColors()
                        : getUnselectedColors()
                }`}>
                <FontAwesomeIcon icon={faThumbsUp} />
                <span>{voteInfo.upvoteCount}</span>
            </button>

            <button
                onClick={() => handleVote(VoteTypeEnum.DOWNVOTE)}
                className={`flex items-center gap-1 px-1 py-1 rounded text-xs transition-colors ${
                    voteInfo.userVote === VoteTypeEnum.DOWNVOTE
                        ? getSelectedColors()
                        : getUnselectedColors()
                }`}>
                <FontAwesomeIcon icon={faThumbsDown} />
                <span>{voteInfo.downvoteCount}</span>
            </button>
        </div>
    );
};
